import permitsSeed from "@/data/permits.generated.json";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Car, Bike, Truck, Caravan, GraduationCap, ArrowRight, RotateCcw, Expand } from "lucide-react";
import { API } from "@/lib/faro";
import Lightbox from "@/components/Lightbox";
import { LANDING_BY_CODE } from "@/data/permitLandings";
import { getHeroSlug, heroSrcSet } from "@/lib/heroImages";

const CATEGORY_META = {
  particulares: { label: "Coche", icon: Car },
  motos: { label: "Motos & ligeros", icon: Bike },
  remolques: { label: "Remolques", icon: Caravan },
  profesionales: { label: "Profesionales", icon: Truck },
  cursos: { label: "Cursos", icon: GraduationCap },
};

const CATEGORY_ORDER = ["particulares", "motos", "remolques", "profesionales", "cursos"];

const FeatureLine = ({ f }) => {
  const parts = f.split(/\*\*(.+?)\*\*/g);
  return (
    <li className="text-sm flex items-start gap-2 text-[var(--faro-text)]">
      <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-[var(--faro-red)] shrink-0" />
      <span>
        {parts.map((seg, i) =>
          i % 2 === 1 ? <strong key={`b-${i}-${seg}`} className="font-black">{seg}</strong> : <React.Fragment key={`t-${i}-${seg}`}>{seg}</React.Fragment>
        )}
      </span>
    </li>
  );
};

function PermitCardFront({ p, catLabel, Icon, isAML, landing, onFlip }) {
  return (
    <div
      style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", position: "relative" }}
      className="permit-card flex flex-col h-full min-h-[440px]"
    >
      <span className="absolute top-0 left-0 h-1.5 w-full bg-[var(--faro-blue)]" />

      <div className={`flex items-start justify-between gap-4 ${isAML ? "sm:pt-5" : ""}`}>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--faro-text-soft)]">
            {catLabel}
          </div>
          <div className="permit-code mt-1">{p.code}</div>
          <h3 className="font-display font-bold text-xl mt-2 text-[var(--faro-text)]">{p.name}</h3>
        </div>
        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[var(--faro-bg)] shrink-0">
          <Icon size={22} className="text-[var(--faro-blue)]" />
        </div>
      </div>

      <p className="mt-4 text-sm leading-relaxed text-[var(--faro-text-soft)]">{p.summary}</p>

      <ul className="mt-4 space-y-1.5 flex-1">
        {p.features.map((f) => <FeatureLine key={f} f={f} />)}
      </ul>

      {landing && (
        <Link
          to={landing}
          data-testid={`permit-landing-${p.code}`}
          className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-[var(--faro-blue)] hover:text-[var(--faro-red)] transition-colors"
        >
          Requisitos y precios en Vigo <ArrowRight size={13} />
        </Link>
      )}
      <div className="mt-4 pt-4 border-t border-[rgba(30,42,143,0.1)] flex items-center justify-between text-sm font-semibold text-[var(--faro-text-soft)]">
        <span>{p.foot ? p.foot : `Edad mínima: ${p.min_age} años`}</span>{" "}
        <button
          type="button"
          onClick={onFlip}
          data-testid={`permit-info-${p.code}`}
          className="inline-flex items-center gap-1 text-[var(--faro-red)] hover:gap-2 transition-all font-semibold cursor-pointer bg-transparent border-0 p-0"
        >
          Infórmate <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

function PermitCard({ p, idx }) {
  const [flipped, setFlipped] = useState(false);
  const [vIdx, setVIdx] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const isAML = p.code === "AML";
  const isAM = p.code === "AM";
  const meta = CATEGORY_META[p.category] || CATEGORY_META.particulares;
  const Icon = isAML ? Car : (isAM ? Bike : meta.icon);
  const catLabel = isAML ? '"Coche sin carnet"' : (isAM ? "Ciclomotor" : meta.label);
  const hasVehicles = Array.isArray(p.vehicle_images) && p.vehicle_images.length > 0;
  const landing = LANDING_BY_CODE[p.code];
  const infoHref = p.code === "Puntos" ? "/recuperacion-de-puntos-vigo#puntos-formulario" : landing || (p.code === "CAP" ? "/cap#cap" : "#contacto");

  // Auto-rotate multiple vehicle images while flipped
  useEffect(() => {
    if (!flipped || !hasVehicles || p.vehicle_images.length < 2) return;
    const t = setInterval(() => {
      setVIdx((i) => (i + 1) % p.vehicle_images.length);
    }, 3500);
    return () => clearInterval(t);
  }, [flipped, hasVehicles, p.vehicle_images]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.35, delay: idx * 0.03 }}
      style={{ perspective: 1200 }}
      className="relative"
      data-testid={`permit-card-${p.code}`}
    >
      {p.code === "AML" && (
        <div
          data-testid="permit-badge-aml"
          className="hidden sm:block absolute -top-4 -left-4 z-20 bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] px-4 py-2 rounded-2xl font-display font-black text-sm shadow-lg pointer-events-none"
          style={{ transform: "rotate(-6deg)" }}
        >
          ¡Únicos con cuadriciclo propio!
        </div>
      )}
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformStyle: "preserve-3d", position: "relative", width: "100%" }}
        className="min-h-[440px] h-full"
      >
        {/* FRONT (in-flow, defines height) */}
        <PermitCardFront p={p} catLabel={catLabel} Icon={Icon} isAML={isAML} landing={landing} onFlip={() => setFlipped(true)} />

        <PermitCardBack
          p={p}
          vIdx={vIdx}
          setVIdx={setVIdx}
          hasVehicles={hasVehicles}
          infoHref={infoHref}
          onUnflip={() => setFlipped(false)}
          onZoom={() => setZoomOpen(true)}
        />
      </motion.div>

      {hasVehicles && (
        <Lightbox
          images={p.vehicle_images}
          startIndex={vIdx}
          open={zoomOpen}
          onClose={() => setZoomOpen(false)}
        />
      )}
    </motion.div>
  );
}

function PermitCardBack({ p, vIdx, setVIdx, hasVehicles, infoHref, onUnflip, onZoom }) {
  return (
    <div
      style={{
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
        transform: "rotateY(180deg)",
        position: "absolute",
        inset: 0,
        background: "var(--faro-blue)",
        color: "#fff",
        borderColor: "transparent",
      }}
      className="permit-card flex flex-col"
      data-testid={`permit-card-back-${p.code}`}
    >
      <span className="absolute top-0 left-0 h-1.5 w-full bg-[var(--faro-yellow)]" />

          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--faro-yellow)]">
                Flota Faro
              </div>
              <h3 className="font-display font-black text-xl mt-1">{(p.code === "CAP" || p.code === "Puntos") ? "Curso" : "Permiso"} {p.code}</h3>
            </div>
            <button
              type="button"
              onClick={onUnflip}
              data-testid={`permit-flip-back-${p.code}`}
              className="w-9 h-9 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors shrink-0"
              aria-label="Volver"
            >
              <RotateCcw size={16} />
            </button>
          </div>

          <div className="flex-1 flex flex-col gap-2 min-h-0">
            {hasVehicles ? (
              <div className="rounded-xl overflow-hidden bg-black/20 flex-1 min-h-[160px] relative">
                <AnimatePresence mode="wait">
                  {(() => {
                    const src = p.vehicle_images[vIdx];
                    const slug = getHeroSlug(src);
                    return (
                      <motion.picture
                        key={src}
                        initial={{ opacity: 0, scale: 1.04 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        className="absolute inset-0 w-full h-full block"
                      >
                        {slug && <source type="image/avif" srcSet={heroSrcSet(slug, "avif")} sizes="(min-width: 1024px) 320px, (min-width: 640px) 45vw, 90vw" />}
                        {slug && <source type="image/webp" srcSet={heroSrcSet(slug, "webp")} sizes="(min-width: 1024px) 320px, (min-width: 640px) 45vw, 90vw" />}
                        <img
                          src={src}
                          alt={`Vehículo de prácticas de Autoescuelas Faro para el permiso ${p.code} en Vigo`}
                          width="640"
                          height="360"
                          loading="lazy"
                          decoding="async"
                          style={{ objectPosition: `center ${p.image_position_y ?? 50}%` }}
                          className={`w-full h-full ${p.image_object_fit === "contain" ? "object-contain" : "object-cover"}`}
                          data-testid={`permit-vehicle-img-${p.code}`}
                        />
                      </motion.picture>
                    );
                  })()}
                </AnimatePresence>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onZoom(); }}
                  aria-label="Ver a pantalla completa"
                  data-testid={`permit-zoom-${p.code}`}
                  className="absolute top-2 right-2 z-10 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur text-white flex items-center justify-center transition-colors"
                >
                  <Expand size={16} />
                </button>
                {p.vehicle_images.length > 1 && (
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                    {p.vehicle_images.map((img, i) => (
                      <button
                        key={img}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setVIdx(i); }}
                        aria-label={`Ver foto ${i + 1}`}
                        data-testid={`permit-vehicle-dot-${p.code}-${i}`}
                        className={`h-1.5 rounded-full transition-all ${i === vIdx ? "w-6 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-xl bg-white/10 flex-1 min-h-[160px] flex items-center justify-center text-center px-4">
                <span className="text-sm text-white/80">Nuestros vehículos {p.name.toLowerCase()} te esperan en el centro.</span>
              </div>
            )}
          </div>

          <a
            href={infoHref}
            onClick={() => setTimeout(onUnflip, 400)}
            data-testid={`permit-contact-${p.code}`}
            className="mt-4 inline-flex items-center justify-center gap-2 bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] font-black uppercase tracking-wider text-xs py-3 rounded-full hover:bg-white transition-colors"
          >
            Infórmate ahora <ArrowRight size={14} />
          </a>
    </div>
  );
}

export default function Permits() {
  const [permits, setPermits] = useState(Array.isArray(permitsSeed) ? permitsSeed : []);
  const [active, setActive] = useState("all");

  useEffect(() => {
    axios.get(`${API}/permits`).then((r) => setPermits(r.data)).catch(() => {});
  }, []);

  const filtered = active === "all" ? permits : permits.filter((p) => p.category === active);

  return (
    <section id="permisos" className="section-pad bg-[var(--faro-bg)]" data-testid="permits-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <span className="eyebrow">Catálogo de permisos</span>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">
              Todos los carnets. Una sola autoescuela.
            </h2>
            <p className="mt-4 text-lg text-[var(--faro-text-soft)]">
              Desde tu primer ciclomotor a 15 años hasta el camión articulado o el autobús.
              Tenemos la flota y los profesores para acompañarte.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              data-testid="permit-filter-all"
              onClick={() => setActive("all")}
              className={`btn-pill text-sm ${active === "all" ? "btn-secondary" : "btn-ghost"}`}
            >
              Todos
            </button>
            {CATEGORY_ORDER.map((c) => {
              const Icon = CATEGORY_META[c].icon;
              return (
                <button
                  key={c}
                  data-testid={`permit-filter-${c}`}
                  onClick={() => setActive(c)}
                  className={`btn-pill text-sm ${active === c ? "btn-secondary" : "btn-ghost"}`}
                >
                  <Icon size={16} /> {CATEGORY_META[c].label}
                </button>
              );
            })}
          </div>
        </div>

        <AnimatePresence mode="popLayout">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p, idx) => (
              <PermitCard key={p.code} p={p} idx={idx} />
            ))}
          </div>
        </AnimatePresence>
      </div>
    </section>
  );
}
